import Vue from 'vue';
import VueApollo from 'vue-apollo';
import DuoAgenticStateManager from 'ee/ai/duo_agentic_chat/components/duo_agentic_chat_state_manager.vue';
import { parseBoolean } from '~/lib/utils/common_utils';
import { __ } from '~/locale';
import { createRouter } from 'ee/ai/duo_agents_platform/router/ai_panel_router';
import DuoChat from 'ee/ai/tanuki_bot/components/duo_chat_state_manager.vue';
import { activeWorkItemIds } from '~/work_items/utils';
import { setAgenticMode } from 'ee/ai/utils';
import store from './tanuki_bot/store';
import { createApolloProvider } from './graphql';
import AIPanel from './components/ai_panel.vue';
import AIPanelEmptyState from './components/ai_panel_empty_state.vue';

function initDuoPanelEmptyState() {
  const el = document.getElementById('duo-chat-panel-empty-state');

  if (!el) {
    return false;
  }

  Vue.use(VueApollo);

  const {
    newTrialPath,
    trialDuration,
    namespaceType,
    isTrialExpired,
    buyAddonPath,
    canBuyAddon,
    canStartTrial,
  } = el.dataset;

  const autoExpand = parseBoolean(el.dataset.autoExpand);
  const apolloProvider = createApolloProvider({ autoExpand });

  return new Vue({
    el,
    name: 'AIPanelEmptyStateApp',
    apolloProvider,
    provide: {
      autoExpand,
      canStartTrial: parseBoolean(canStartTrial),
      isTrialExpired: parseBoolean(isTrialExpired),
      newTrialPath,
      trialDuration,
      namespaceType,
      buyAddonPath,
      canBuyAddon: parseBoolean(canBuyAddon),
    },
    render(renderElement) {
      return renderElement(AIPanelEmptyState);
    },
  });
}

export function initDuoPanel() {
  const el = document.getElementById('duo-chat-panel');

  if (!el) {
    return initDuoPanelEmptyState();
  }

  const {
    userId,
    projectId,
    projectPath,
    namespaceId,
    rootNamespaceId,
    resourceId,
    metadata,
    userModelSelectionEnabled,
    agenticAvailable,
    classicAvailable,
    forceAgenticModeForCoreDuoUsers,
    agenticUnavailableMessage,
    chatTitle,
    chatDisabledReason,
    duoSettingsPath,
    defaultNamespaceSelected,
    preferencesPath,
    isTrial,
    buyAddonPath,
    canBuyAddon,
    trialActive,
    subscriptionActive,
    exploreAiCatalogPath,
    autoExpand,
    containerType,
  } = el.dataset;

  if (parseBoolean(forceAgenticModeForCoreDuoUsers)) {
    setAgenticMode({ agenticMode: true, saveCookie: true });
  }

  Vue.use(VueApollo);

  const apolloProvider = createApolloProvider({ autoExpand: parseBoolean(autoExpand) });

  const canConfigureDuoSettings = Boolean(duoSettingsPath);
  const isTrialExpired = parseBoolean(el.dataset.isTrialExpired);
  const isDuoDisabledForAdmin = chatDisabledReason && canConfigureDuoSettings;
  const isDuoDisabledNonAdmin = parseBoolean(el.dataset.isDuoDisabledNonAdmin);
  const shouldShowBlockedState = isDuoDisabledForAdmin || isDuoDisabledNonAdmin || isTrialExpired;

  // Configure chat-specific values in a single configuration object
  const chatConfiguration = {
    agenticComponent: DuoAgenticStateManager,
    classicComponent: DuoChat,
    agenticTitle: chatTitle || __('GitLab Duo Agentic Chat'),
    classicTitle: __('GitLab Duo Chat'),
    defaultProps: {
      isEmbedded: true,
      userId,
      projectId,
      projectPath,
      namespaceId,
      rootNamespaceId,
      resourceId,
      metadata,
      agenticUnavailableMessage,
      userModelSelectionEnabled: parseBoolean(userModelSelectionEnabled),
      chatDisabledReason,
      isDuoDisabled: Boolean(chatDisabledReason),
      isAgenticAvailable: parseBoolean(agenticAvailable),
      isClassicAvailable: parseBoolean(classicAvailable),
      forceAgenticModeForCoreDuoUsers: parseBoolean(forceAgenticModeForCoreDuoUsers),
      chatTitle,
      canConfigureDuoSettings,
      duoSettingsPath,
      defaultNamespaceSelected: parseBoolean(defaultNamespaceSelected),
      preferencesPath,
      isTrial: parseBoolean(isTrial),
      isTrialExpired,
      buyAddonPath,
      canBuyAddon: parseBoolean(canBuyAddon),
      trialActive: parseBoolean(trialActive ?? 'false'),
      subscriptionActive: parseBoolean(subscriptionActive ?? 'false'),
      exploreAiCatalogPath,
      isDuoDisabledNonAdmin,
      containerType: containerType || (projectId ? 'project' : 'group'),
      isDuoDisabledForAdmin,
      shouldShowBlockedState,
    },
  };

  const router = createRouter('/', 'user', chatConfiguration);

  return new Vue({
    el,
    name: 'DuoPanel',
    store: store(),
    router,
    apolloProvider,
    provide: {
      isSidePanelView: true,
      badgeType: 'beta',
      // Inject chat configuration directly to components that need it
      chatConfiguration,
    },
    render(createElement) {
      const latestActiveWorkItemId = activeWorkItemIds.value[activeWorkItemIds.value.length - 1];
      return createElement(AIPanel, {
        props: {
          name: 'AiPanel',
          userId,
          projectId,
          namespaceId,
          rootNamespaceId,
          resourceId: latestActiveWorkItemId ?? resourceId,
          metadata,
          userModelSelectionEnabled: parseBoolean(userModelSelectionEnabled),
          chatDisabledReason,
          shouldShowBlockedState,
        },
      });
    },
  });
}
